import axios from 'axios';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useSnackbar } from "../contexts/SnackbarContext";
import { parseError } from "../services/http.service";
import { getCookie } from "../utils/cookies";
import { accessTokenKey } from "./AuthInterceptor";

interface User {
    id: number;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
    avatarBase64?: string;
    name?: string;
    surname?: string;
    position?: string;
    phoneNumber?: string;
    group?: Array<{
        id: number;
        name: string;
        createdAt: string;
    }>;
}

interface UserContextType {
    user: User | null;
    fetchUserData: () => Promise<void>;
    refreshConversations: number;
    triggerConversationRefresh: () => void;
    resetUserData: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoadingUser, setIsLoadingUser] = useState(false);
    const [refreshConversations, setRefreshConversations] = useState(0)
    const {showSnackbar} = useSnackbar();
    const fetchInitiatedRef = React.useRef(false);

    const fetchUserData = async () => {
        if (isLoadingUser) {
            return;
        }

        const token = getCookie(accessTokenKey);
        if (!token) return;

        setIsLoadingUser(true);
        try {
            const response = await axios.get<User>(`/users/currentUserDetails`);
            setUser(response.data);
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            setIsLoadingUser(false);
        }
    };

    const resetUserData = () => {
        setUser(null);
        fetchInitiatedRef.current = false;
    };

    const triggerConversationRefresh = () => {
        setRefreshConversations(prev => prev + 1);
    }

    useEffect(() => {
        if (!user && !isLoadingUser && !fetchInitiatedRef.current) {
            fetchInitiatedRef.current = true;
            fetchUserData();
        }
    }, []);

    return (
        <UserContext.Provider
            value={{user, fetchUserData, refreshConversations, triggerConversationRefresh, resetUserData}}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
